<template>
  <v-card outlined class="statement-preview">
    <div class="d-flex align-center px-3 pt-2">
      <h4 class="mr-auto">Preview</h4>
      <span class="text-caption grey--text mr-2">{{ kindPosition }}</span>
      <v-btn icon small :disabled="kindIndex <= 0" @click="stepKind(-1)" aria-label="Previous statement">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <v-btn icon small :disabled="kindIndex >= allKinds.length - 1" @click="stepKind(1)" aria-label="Next statement">
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
      <v-btn icon small :disabled="!pdfBytes || isEmailKind" @click="openFullSize" aria-label="Open full size">
        <v-icon small>mdi-open-in-new</v-icon>
      </v-btn>
    </div>

    <v-chip-group
      v-if="kinds.length > 0"
      :value="currentKind"
      mandatory
      show-arrows
      active-class="primary--text"
      class="px-2"
      @change="selectKind"
    >
      <v-chip
        v-for="kind in allKinds"
        :key="kind.key"
        :value="kind.key"
        small
        outlined
        :color="affectedKinds.includes(kind.key) ? 'warning' : undefined"
      >
        {{ kind.label }}
      </v-chip>
    </v-chip-group>
    <div class="text-caption grey--text px-3 mt-n1 mb-1">{{ currentKindDescription }}</div>

    <div
      ref="viewport"
      class="statement-preview__viewport"
      @wheel="onWheel"
    >
      <v-overlay absolute :value="loading" opacity="0.15" color="white">
        <v-progress-circular indeterminate color="primary" />
      </v-overlay>

      <v-card v-if="isEmailKind" flat class="ma-3 pa-3 email-preview">
        <div class="text-caption"><strong>To:</strong> taxpayer@example.com</div>
        <div v-if="statementConfig.bccEmailAddress" class="text-caption">
          <strong>Bcc:</strong> {{ statementConfig.bccEmailAddress }}
        </div>
        <div class="text-caption mb-2">
          <strong>Subject:</strong> {{ statementConfig.subjectLine || '(no subject)' }}
        </div>
        <v-divider class="mb-2" />
        <div class="email-preview__body">{{ statementConfig.subjectBody || '(no message)' }}</div>
        <v-divider class="my-2" />
        <div class="text-caption grey--text">
          <v-icon small>mdi-paperclip</v-icon> Tax statement (PDF)
        </div>
      </v-card>

      <v-alert v-else-if="error" type="error" dense text class="ma-3">
        {{ error }}
        <v-btn x-small outlined color="error" class="ml-2" @click="render({ isEdit: false })">Try again</v-btn>
      </v-alert>

      <template v-else-if="pdfSrc">
        <pdf
          v-for="page in numPages"
          :key="`${renderId}-${page}`"
          :src="pdfSrc"
          :page="page"
          scale="page-width"
          :resize="true"
          :annotation="false"
          :text="false"
          class="statement-preview__page"
        />
      </template>
    </div>

    <v-alert
      v-if="warning"
      type="warning"
      dense
      text
      icon="mdi-information-outline"
      class="ma-2 mb-2 statement-preview__warning"
    >
      {{ warning }}
      <div class="mt-1">
        <v-btn
          v-for="key in affectedKinds"
          :key="key"
          x-small
          outlined
          color="warning darken-2"
          class="mr-1 mt-1"
          @click="selectKind(key)"
        >
          Show {{ labelFor(key) }}
        </v-btn>
      </div>
    </v-alert>
  </v-card>
</template>

<script>
import pdf from 'pdfvuer';
import { clients } from '../../util/clients';
import {
  EMAIL_FIELDS, EMAIL_KIND, changedFields, changedKinds, impactWarning, previewRequestConfig,
} from '../../util/shared/statement-preview';

const { backendRest } = clients.direct;

const DEBOUNCE_MS = 450;
// Past this, the backend stops honouring previewAt (it allows a day of drift), so take a new
// one and treat the next render as a fresh baseline rather than comparing across it.
const PREVIEW_AT_MAX_AGE_MS = 12 * 60 * 60 * 1000;
// How far past the end of a statement the wheel has to push before moving to the next kind.
const WHEEL_OVERSCROLL_PX = 150;

export default {
  name: 'StatementPreviewPane',
  components: { pdf },
  props: {
    // The live (unsaved) Statements settings form values.
    statementConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    // One "as of" time for the visit. Statements print the time they were run, so renders at
    // different minutes would otherwise differ on every kind (see statementPreview.js).
    previewAt: new Date().toISOString(),
    kinds: [],
    currentKind: null,
    pdfBytes: null,
    pdfSrc: null,
    pdfDoc: null,
    numPages: 0,
    renderId: 0,
    loading: false,
    error: null,
    // Snapshot of the settings the current fingerprints were rendered from.
    renderedConfig: null,
    // Kind keys the most recent edit changed; null until the first edit.
    lastEditImpact: null,
    debounceTimer: null,
    requestSeq: 0,
    wheelOverscroll: 0,
    destroyed: false,
  }),
  computed: {
    allKinds() {
      return [...this.kinds, EMAIL_KIND];
    },
    kindIndex() {
      return this.allKinds.findIndex((k) => k.key === this.currentKind);
    },
    kindPosition() {
      if (this.kindIndex < 0) return '';
      return `${this.kindIndex + 1} of ${this.allKinds.length}`;
    },
    currentKindDescription() {
      const kind = this.allKinds[this.kindIndex];
      return kind ? kind.description : '';
    },
    isEmailKind() {
      return this.currentKind === EMAIL_KIND.key;
    },
    affectedKinds() {
      return this.lastEditImpact || [];
    },
    warning() {
      if (!this.lastEditImpact) return null;
      return impactWarning(this.currentKind, this.lastEditImpact, this.allKinds);
    },
  },
  watch: {
    statementConfig: {
      deep: true,
      handler() {
        this.scheduleRender();
      },
    },
  },
  mounted() {
    this.render({ isEdit: false });
  },
  beforeDestroy() {
    this.destroyed = true;
    // Any response still in flight is ignored from here on.
    this.requestSeq += 1;
    clearTimeout(this.debounceTimer);
    this.destroyPdfDoc();
  },
  methods: {
    labelFor(key) {
      const kind = this.allKinds.find((k) => k.key === key);
      return kind ? kind.label : key;
    },
    snapshot() {
      return JSON.parse(JSON.stringify(this.statementConfig || {}));
    },
    scheduleRender() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.render({ isEdit: true }), DEBOUNCE_MS);
    },
    selectKind(key) {
      if (!key || key === this.currentKind) return;
      this.currentKind = key;
      this.wheelOverscroll = 0;
      if (!this.isEmailKind) this.render({ isEdit: false });
    },
    stepKind(step) {
      const next = this.allKinds[this.kindIndex + step];
      if (next) this.selectKind(next.key);
    },
    // Pushing the wheel well past the top or bottom of a statement moves to the previous or
    // next kind. Only beside the settings (lg and up), and only when the statement scrolls,
    // so it never takes over scrolling the page itself.
    onWheel(event) {
      const el = this.$refs.viewport;
      if (!el || !this.$vuetify.breakpoint.lgAndUp || el.scrollHeight <= el.clientHeight + 2) return;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
      const atTop = el.scrollTop <= 0;
      const pushingDown = event.deltaY > 0 && atBottom && this.kindIndex < this.allKinds.length - 1;
      const pushingUp = event.deltaY < 0 && atTop && this.kindIndex > 0;
      if (!pushingDown && !pushingUp) {
        this.wheelOverscroll = 0;
        return;
      }
      event.preventDefault();
      this.wheelOverscroll += Math.abs(event.deltaY);
      if (this.wheelOverscroll < WHEEL_OVERSCROLL_PX) return;
      this.stepKind(pushingDown ? 1 : -1);
      this.$nextTick(() => { el.scrollTop = pushingDown ? 0 : el.scrollHeight; });
    },
    openFullSize() {
      if (!this.pdfBytes) return;
      const url = URL.createObjectURL(new Blob([this.pdfBytes], { type: 'application/pdf' }));
      window.open(url, '_blank');
      // The new tab has its own copy once it has loaded.
      setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
    },
    destroyPdfDoc() {
      if (this.pdfDoc) this.pdfDoc.destroy();
      this.pdfDoc = null;
    },
    showPdf(base64, seq) {
      const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
      this.pdfBytes = bytes;
      // pdf.js takes ownership of the buffer it is given, so hand it a copy.
      const task = pdf.createLoadingTask({ data: bytes.slice() });
      task.then((doc) => {
        if (seq !== this.requestSeq || this.destroyed) {
          doc.destroy();
          return;
        }
        this.destroyPdfDoc();
        this.pdfDoc = doc;
        this.numPages = doc.numPages;
        this.renderId += 1;
        this.pdfSrc = task;
      }).catch(() => {
        if (seq === this.requestSeq) this.error = 'The preview could not be displayed.';
      });
    },
    async render({ isEdit }) {
      clearTimeout(this.debounceTimer);
      const next = this.snapshot();

      let baseline = this.renderedConfig;
      if (Date.now() - new Date(this.previewAt).getTime() > PREVIEW_AT_MAX_AGE_MS) {
        this.previewAt = new Date().toISOString();
        baseline = null;
      }
      // Compared on every render, not only edit-triggered ones: switching kinds while an edit
      // is still debouncing (or in flight) sends that edit too, and its impact must not be lost.
      // With no baseline yet (first load still running, or it failed) an edit just renders.
      const fields = baseline ? changedFields(baseline, next) : [];
      if (isEdit && baseline && fields.length === 0) return;

      // Email-only edits never change a PDF, so there is nothing to render or compare.
      if (isEdit && fields.length > 0 && fields.every((f) => EMAIL_FIELDS.includes(f))) {
        this.renderedConfig = next;
        this.lastEditImpact = [EMAIL_KIND.key];
        return;
      }

      this.requestSeq += 1;
      const seq = this.requestSeq;
      this.loading = true;
      try {
        await this.$store.dispatch('reAuth');
        const { data } = await backendRest.post('/taxInquiry/statement-preview', {
          statementConfig: previewRequestConfig(next),
          kind: this.isEmailKind ? undefined : this.currentKind,
          previewAt: this.previewAt,
        });
        // A newer request already went out; its answer is the one that counts.
        if (seq !== this.requestSeq) return;

        if (fields.length > 0) {
          const affected = changedKinds(this.kinds, data.kinds);
          if (fields.some((f) => EMAIL_FIELDS.includes(f))) affected.push(EMAIL_KIND.key);
          this.lastEditImpact = affected;
        }
        this.kinds = data.kinds;
        this.renderedConfig = next;
        if (!this.isEmailKind) this.currentKind = data.kind;
        this.error = null;
        if (!this.isEmailKind) this.showPdf(data.pdf, seq);
      } catch (err) {
        if (seq !== this.requestSeq) return;
        const message = err && err.response && err.response.data && err.response.data.message;
        this.error = message || 'The preview could not be rendered.';
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.statement-preview {
  /* Fixed height column: the statement area takes whatever the warning leaves, so a long
     warning (several statement kinds) never runs off the bottom of the screen. */
  display: flex;
  flex-direction: column;
  height: calc(100vh - 210px);
  min-height: 480px;
}

.statement-preview__viewport {
  position: relative;
  flex: 1 1 auto;
  min-height: 200px;
  overflow-y: auto;
  background: #e0e0e0;
}

.statement-preview__warning {
  flex: 0 0 auto;
}

.statement-preview__page {
  margin: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  background: white;
}

.email-preview {
  background: white;
}

.email-preview__body {
  white-space: pre-wrap;
  font-size: 0.85rem;
}
</style>
